import {
  FAVORITE_URL_PARAM_NAME,
  ITEM_TO_FAVORITE,
  RETURN_FAVORITE_TOKEN_NAME,
  verifyReturnToken,
} from '@app/components/SignUpGuard/utils';
import { FavoriteStatus } from '@app/data/clients/listsClient/types';
import { HomepageWork } from '@app/data/clients/monolithClients/explore/types';
import {
  addToFavorite,
  favoriteStatus,
  removeFromFavorite,
} from '@app/utils/favorites';
import { UserInfo } from '@redbubble/next/types';
import { useUserInfo } from '@redbubble/next-provider';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useState } from 'react';
import { useSignUpGuardContext } from './SignUpGuardContext';

// Currently this only works with works as it was initially built for the homepage works
// carousel. The intent is for this to be extended to also allow managing inventoryItems
// in lists as well as supported by the lists api. This will need to be extended in the future to enable this.

export const useFavoriteAction = ({
  work,
  initialFavoriteStatuses,
  onAddToFavorite,
}: {
  //this needs to be able to handle inventoryItems
  work: HomepageWork;
  initialFavoriteStatuses: FavoriteStatus[] | null;
  onAddToFavorite?: () => void;
}) => {
  //this needs to get id depending on work or inventoryItem
  const { id: workId } = work;

  const router = useRouter();
  const { signUpGuard, setSignUpGuard } = useSignUpGuardContext();
  const [{ isLoggedIn }] = useUserInfo<UserInfo>();

  const [isFavorited, setIsFavorited] = useState<boolean>(false);
  const [favoriteStatuses, setFavoriteStatuses] = useState(
    initialFavoriteStatuses
  );

  useEffect(() => {
    if (initialFavoriteStatuses) {
      setFavoriteStatuses(initialFavoriteStatuses);
    }
  }, [initialFavoriteStatuses]);

  const toggleFavorite = useCallback(() => {
    if (!isLoggedIn) {
      setSignUpGuard({
        ...signUpGuard,
        open: true,
        actionToken: RETURN_FAVORITE_TOKEN_NAME,
        //also be able to accept inventoryItemId
        itemToFavorite: workId.toString(),
      });
      return;
    }

    if (isFavorited) {
      //this needs to be able to accept inventoryItems also
      removeFromFavorite({ favorite: { workId: workId.toString() } }).then(
        (newFavoriteStatus) => {
          if (newFavoriteStatus && !newFavoriteStatus.isFavorited) {
            setIsFavorited(false);
          }
        }
      );
    } else {
      //this needs to be able to accept inventoryItems also
      addToFavorite({ favorite: { workId: workId.toString() } }).then(
        (newFavoriteStatus) => {
          if (newFavoriteStatus && newFavoriteStatus.isFavorited) {
            setIsFavorited(true);
            if (onAddToFavorite) {
              onAddToFavorite();
            }
          }
        }
      );
    }
  }, [
    isLoggedIn,
    isFavorited,
    setSignUpGuard,
    signUpGuard,
    workId,
    onAddToFavorite,
  ]);

  // Handle return from login/signup flow
  useEffect(() => {
    const {
      [FAVORITE_URL_PARAM_NAME]: addToFavoriteToken,
      [ITEM_TO_FAVORITE]: itemToFavorite,
    } = router.query;
    if (!itemToFavorite) return;

    if (favoriteStatuses && itemToFavorite === workId.toString()) {
      const newQuery = { ...router.query };
      delete newQuery[FAVORITE_URL_PARAM_NAME];
      delete newQuery[ITEM_TO_FAVORITE];

      if (typeof addToFavoriteToken === 'string' && addToFavoriteToken) {
        const addToFavoriteMatches = verifyReturnToken(
          addToFavoriteToken,
          RETURN_FAVORITE_TOKEN_NAME
        );

        if (addToFavoriteMatches && !isFavorited) {
          toggleFavorite();
        }

        router.replace(
          {
            query: newQuery,
          },
          undefined,
          { shallow: true }
        );
      }
    }
  }, [favoriteStatuses, toggleFavorite, isFavorited, workId, router]);

  // Check if currently selected item is in favorite
  useEffect(() => {
    //this needs to check work or inventoryItem
    const isIncludedFavorited =
      !!favoriteStatuses && favoriteStatus(workId, favoriteStatuses);

    setIsFavorited(isIncludedFavorited);
  }, [workId, favoriteStatuses]);

  return {
    isFavorited,
    toggleFavorite,
  };
};
